import { useEffect, useRef, useState } from 'react'
import styles from './Navbar.module.scss'
import { LogoutIcon, MenuIcon } from '@icons/utils'
import { useMsal } from '@azure/msal-react'
import { cn } from '@src/utils/cn.ts'
import { LxClickAbleIcon } from '@components/icon/clickAbleIcon.tsx'
import { LxIcon } from '@components/icon/Icon.tsx'
import LxMenu from '@src/pages/layout/Menu.tsx'
import { useLayout } from '@logic/contexts/LayoutContext.tsx'
import { LxHandleKeyDown } from '@src/utils/Accessbility'
import { useAppContext } from '@src/logic/contexts/AppStore/AppContext'
import { LOCAL_STORAGE_KEYS, ACCOUNT_CHANGE_KEYS } from '@src/utils/constants'

const APP_BASE_URL = import.meta.env.VITE_APP_DIST_BASE_URL || 'https://strg.app.luxai.com/apps-distribution'

export const LxNavbar = () => {
  const { instance } = useMsal()
  const { globals } = useAppContext()
  const { setShouldOpenMobileNav, shouldOpenMobileNav } = useLayout()
  const isMultiAccount = localStorage.getItem('multiAccounts') === 'true'

  const [educatorAppUrl, setEducatorAppUrl] = useState('')
  const [learnerAppUrl, setLearnerAppUrl] = useState('')
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [activeIndex, setActiveIndex] = useState(-1)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const menuItemsRef = useRef<(HTMLAnchorElement | null)[]>([])

  useEffect(() => {
    const fetchAppDetails = async (appType: string, setUrl: Function) => {
      try {
        const response = await fetch(`${APP_BASE_URL}/${appType}/app.json`)
        const data = await response.json()
        setUrl(`${APP_BASE_URL}/${appType}/${data.filename}`)
      } catch (error) {
        console.error(`Failed to fetch ${appType} app details`, error)
      }
    }

    fetchAppDetails('EducatorApp', setEducatorAppUrl)
    fetchAppDetails('LearnerApp', setLearnerAppUrl)
  }, [])

  // for cross tab logout and switch account handling
  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === 'user_logged_out' || event.key === 'account_switched') {
         window.location.replace(`${import.meta.env.VITE_APP_BASE_URI}/`)
      }
    }

    window.addEventListener('storage', handleStorageChange)
    return () => window.removeEventListener('storage', handleStorageChange)
  }, [])

  const handleLogout = async () => {
    // userId, AccoutnId - will set during useTokenManagement context - Home component
    // msalClientId, msalClientUrl, apiUrl- will be set in login page
    // msalB2CId - will be set after login - App component
    LOCAL_STORAGE_KEYS.forEach((key) => localStorage.removeItem(key))
    // issue in msal, sometimes this below status is not cleared so manually clearing the msal.interaction.status
    sessionStorage.removeItem('msal.interaction.status')
    localStorage.setItem('user_logged_out', Date.now().toString()) // for cross-tab logout handling
    await instance.logoutRedirect()
  }

  // Switch Account: only clears UserId + AccountId, then hard refreshes
  const handleSwitchAccount = () => {
    ACCOUNT_CHANGE_KEYS.forEach((key) => localStorage.removeItem(key))
     window.location.replace(`${import.meta.env.VITE_APP_BASE_URI}/`)
  }

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false)
        setActiveIndex(-1)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleDropdownKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const menuItems = [educatorAppUrl, learnerAppUrl].filter(Boolean)

    switch (event.key) {
      case 'Enter':
      case ' ':
        event.preventDefault()
        if (!isDropdownOpen) {
          setIsDropdownOpen(true)
          setActiveIndex(0)
        } else if (activeIndex >= 0) {
          menuItemsRef.current[activeIndex]?.click()
        }
        break
      case 'Escape':
        setIsDropdownOpen(false)
        setActiveIndex(-1)
        break
      case 'ArrowDown':
        event.preventDefault()
        if (!isDropdownOpen) {
          setIsDropdownOpen(true)
          setActiveIndex(0)
        } else {
          setActiveIndex((prev) => (prev + 1) % menuItems.length)
        }
        break
      case 'ArrowUp':
        event.preventDefault()
        if (isDropdownOpen) {
          setActiveIndex((prev) => (prev - 1 + menuItems.length) % menuItems.length)
        }
        break
      case 'Tab':
        if (isDropdownOpen) {
          setIsDropdownOpen(false)
          setActiveIndex(-1)
        }
        break
    }
  }

  return (
    <div className={cn(styles.layoutNavbar, 'ignoreOnPrint')}>
      <div className={styles.layoutNavbarleftText}>
        <h5 role='heading'> {globals.accountName ?? ''}</h5>
      </div>
      <div className={styles.navbarNavigation} role='navigation' aria-label='Top'>
        <div
          ref={dropdownRef}
          className={styles.appDownloaddropdown}
          onKeyDown={handleDropdownKeyDown}
          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
          role='button'
          aria-haspopup='true'
          aria-expanded={isDropdownOpen}
          tabIndex={0}
        >
          <h5 id='downloads-label'>Downloads</h5>
          <div className={styles.appDownloaddropdownContent} style={{ display: isDropdownOpen ? 'block' : 'none' }} role='menu' aria-labelledby='downloads-label'>
            {educatorAppUrl && (
              <a
                href={educatorAppUrl}
                ref={(el) => (menuItemsRef.current[0] = el)}
                target='_blank'
                rel='noopener noreferrer'
                role='menuitem'
                tabIndex={activeIndex === 0 ? 0 : -1}
                aria-label='Download Educator App'
              >
                Educator App
              </a>
            )}
            {learnerAppUrl && (
              <a
                href={learnerAppUrl}
                ref={(el) => (menuItemsRef.current[1] = el)}
                target='_blank'
                rel='noopener noreferrer'
                role='menuitem'
                tabIndex={activeIndex === 1 ? 0 : -1}
                aria-label='Download Learner App'
              >
                Learner App
              </a>
            )}
          </div>
        </div>
        <a href='https://luxai.com/qtrobot-training-resources-for-special-needs-education-schools/' target='_blank' className={styles.support}>
          <h5 role='button' aria-label='Tutorials'>
            Tutorials
          </h5>
        </a>
        <a href='https://luxai.com/contact/' target='_blank' className={styles.support}>
          <h5 role='button' aria-label='Support'>
            Support
          </h5>
        </a>
        {isMultiAccount && (
          <div className={styles.switchAccountButton} onClick={handleSwitchAccount} tabIndex={0} onKeyDown={(event) => LxHandleKeyDown(event, handleSwitchAccount)}>
            <h5 role='button' aria-label='Switch Account'>
              Switch Account
            </h5>
          </div>
        )}
        <div className={styles.logoutButton} onClick={handleLogout} tabIndex={0} onKeyDown={(event) => LxHandleKeyDown(event, handleLogout)}>
          <h5 role='button' aria-label='Logout'>
            Logout
          </h5>
          <LxIcon icon={LogoutIcon}></LxIcon>
        </div>
      </div>
      <div className={styles.mobileNavbarNavigation}>
        <LxClickAbleIcon icon={MenuIcon} customViewBox={'0 0 50 50'} onClick={() => setShouldOpenMobileNav(true)}></LxClickAbleIcon>
        {shouldOpenMobileNav && (
          <div className={styles.menuNav}>
            <LxMenu mobileVersion={true} />
          </div>
        )}
      </div>
    </div>
  )
}

export default LxNavbar
